import * as React from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";

/**
 * Hafif modal primitive — radix bağımsız, refund/audit gibi yerlerde kullanılır.
 *
 * Pattern:
 *   const [open, setOpen] = useState(false);
 *   <Dialog open={open} onOpenChange={setOpen}>
 *     <DialogContent>
 *       <DialogHeader title="İade" description="..." />
 *       <div>...</div>
 *       <DialogFooter>
 *         <Button>OK</Button>
 *       </DialogFooter>
 *     </DialogContent>
 *   </Dialog>
 *
 * Sınırlamalar (kasıtlı sade):
 * - Tek seferde tek dialog (bu uygulama scope'unda yeter).
 * - Focus trap yok; klavye sekmesi için browser default davranışı.
 * - Esc + backdrop click ile kapanır.
 */
type DialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  /** true ise backdrop click'le kapanmaz — destructive akışlarda yanlış kapatmayı önler. */
  disableBackdropClose?: boolean;
};

export function Dialog({
  open,
  onOpenChange,
  children,
  disableBackdropClose,
}: DialogProps) {
  const tCommon = useT("common");
  React.useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onOpenChange(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm animate-in fade-in"
      onClick={() => {
        if (!disableBackdropClose) onOpenChange(false);
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-lg overflow-hidden rounded-lg border bg-background shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="absolute end-3 top-3 rounded-md p-1 text-muted-foreground transition hover:bg-muted hover:text-foreground"
          aria-label={tCommon("close")}
        >
          <X className="h-4 w-4" />
        </button>
        {children}
      </div>
    </div>,
    document.body,
  );
}

export function DialogContent({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn("max-h-[80vh] overflow-y-auto p-6", className)}>{children}</div>
  );
}

export function DialogHeader({
  title,
  description,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
}) {
  return (
    <div className="mb-4 space-y-1">
      <h2 className="text-base font-semibold tracking-tight">{title}</h2>
      {description && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}
    </div>
  );
}

export function DialogFooter({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "mt-6 flex flex-col-reverse gap-2 border-t pt-4 sm:flex-row sm:justify-end",
        className,
      )}
    >
      {children}
    </div>
  );
}
